"use client";
import usePhotos from "@/hooks/usePhotos";
import { createContext, useContext, useEffect, useState } from "react";
import { useGalleryContext } from "./GalleryContext";
import { PhotoI } from "@/interfaces/galleries/Galleries";
import { notifyError, notifySuccess } from "@/utils/NotificationFunctions";
import {
  addTagToPhoto,
  getDiscussionsByPhotoId,
  getTagsByPhotoId,
  removeTagFromPhoto,
} from "@/services/PhotosService";
import { newTagI, tagI } from "@/interfaces/Photo";
import { useUserContext } from "./UserContext";
import { useParams, useRouter } from "next/navigation";

type Params = {
  year: string;
  index: string;
};

interface PhotoViewContextI {
  comments: Array<any>;
  getComments: () => void;
  photoId: string;
  setPhotoId: React.Dispatch<React.SetStateAction<string>>;
  author: any;
  year: string;
  index: string;
  loadingAuthor: boolean;
  photo: PhotoI | null;
  discussions: Array<any>;
  getDiscussions: () => void;
  addNewTag: (param: newTagI) => void;
  removeTag: (param: tagI) => void;
  tags: Array<tagI>;
  totalComments: number;
}

const PhotoViewContext = createContext<PhotoViewContextI>({
  comments: [],
  getComments: () => {},
  photoId: "",
  setPhotoId: () => {},
  author: {},
  index: "",
  year: "",
  photo: null,
  loadingAuthor: true,
  discussions: [],
  getDiscussions: () => {},
  addNewTag: () => {},
  removeTag: () => {},
  tags: [],
  totalComments: 0,
});

export function usePhotoViewContext() {
  return useContext(PhotoViewContext);
}

const PhotoViewContextProvider = (props: any) => {
  const [photoId, setPhotoId] = useState<string>("");
  const [comments, setComments] = useState<Array<any>>([]);
  const [totalComments, setTotalComments] = useState<number>(0);
  const [discussions, setDiscussions] = useState<Array<any>>([]);
  const [tags, setTags] = useState<Array<tagI>>([]);
  const { year, index } = useParams<Params>();
  //index will now be the photoId

  const { viewPhoto, getCreator, getPhotoComments } = usePhotos();

  const [author, setAuthor] = useState<any>();
  const [photo, setPhoto] = useState<PhotoI>();
  const [loadingAuthor, setLoadingAuthor] = useState<boolean>(true);
  const { photos, years, gallery, galleryLoader } = useGalleryContext();
  const { userToken } = useUserContext();

  const router = useRouter();

  const getComments = () => {
    getPhotoComments(photoId).then((res) => {
      setComments(res.data);
      setTotalComments(res.totalComments || 0);
    });
  };

  const getDiscussions = () => {
    getDiscussionsByPhotoId(photoId).then((res) => {
      setDiscussions(res.data);
    });
  };

  const getTags = () => {
    getTagsByPhotoId(photoId)
      .then((res) => {
        if (res && res.status === 200) {
          setTags(res.tags);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  // In the following function we validate that the params from the url matches one photo
  // We validate the year, if year doesn't exist, we go back to the gallery
  // if the year exists, but index doesn't we push the user to the first pic of the year.
  const getActualPhoto = async () => {
    if (year && index && photos[year]) {
      const photoFind = photos[year].find((ph) => ph._id === index);
      if (photoFind) {
        setPhoto(photoFind);
        setPhotoId(photoFind._id);
        viewPhoto(photoFind._id);
        if (!author || (author && author._id !== photoFind.authorId)) {
          setLoadingAuthor(true);
          await getCreator(photoFind.authorId).then((res) => {
            setAuthor(res.data);
            setLoadingAuthor(false);
          });
        }
        if (photoFind.authorId === null) {
          setAuthor("UNREGISTERED");
        }
      } else {
        notifyError("The photo that you are searching does not exist.");
        const firstPhoto = photos[year][0];
        if (firstPhoto) {
          router.push(`../${year}/${firstPhoto._id}`);
        }
      }
    } else {
      notifyError(
        "The year that you are searching, does not exist on the actual gallery."
      );
      router.push(`../${years[0]}/0`);
    }
  };

  const addNewTag = (tag: newTagI) => {
    // setTags((prev) => [...prev, tag]);
    if (photo) {
      addTagToPhoto(photo._id, tag, userToken).then((res) => {
        if (res && res.status === "Success") {
          getTags();
          notifySuccess("Tag added successfully");
        }
      });
    }
  };

  const removeTag = (tag: tagI) => {
    // setTags((prev) => [...prev, tag]);
    if (photo) {
      removeTagFromPhoto(photo._id, tag, userToken).then((res) => {
        if (res && res.status === "Success") {
          getTags();
          notifySuccess("Tag removed successfully");
        }
      });
    }
  };

  useEffect(() => {
    if (photoId) {
      getComments();
      getDiscussions();
      getTags();
    }
  }, [photoId]);

  useEffect(() => {
    if (!galleryLoader) {
      getActualPhoto();
    }
  }, [year, index, photos, router, galleryLoader]);

  const values: any = {
    comments,
    getComments,
    setPhotoId,
    year,
    index,
    author,
    photo,
    loadingAuthor,
    discussions,
    getDiscussions,
    addNewTag,
    tags,
    removeTag,
    totalComments,
  };

  return (
    <PhotoViewContext.Provider value={values}>
      {props.children}
    </PhotoViewContext.Provider>
  );
};

export default PhotoViewContextProvider;
